import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Search, Command, Clock, Sparkles, Trash2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Message } from '../../types';
import { ChatMessage } from './ChatMessage';
import { useAuthStore } from '../../store/authStore';
import { useChatStore } from '../../store/chatStore';

// Comandos rápidos para el chat
const QUICK_COMMANDS = {
  '/ayuda': 'Muestra todos los comandos disponibles',
  '/limpiar': 'Limpia la conversación actual',
  '/ejemplo': 'Muestra un ejemplo de pregunta',
  '/reiniciar': 'Reinicia completamente la conversación'
};

const EmptyState = ({ message, subMessage }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-center text-gray-500 dark:text-gray-400 mt-8 px-4"
  >
    <p className="text-lg font-medium mb-2">{message}</p>
    <p className="text-sm">{subMessage}</p>
  </motion.div>
);

const MessageInput = ({
  value,
  onChange,
  onSubmit,
  isLoading,
  onCommandSelect,
  placeholder
}) => {
  const [showCommands, setShowCommands] = useState(false);
  const inputRef = useRef(null);

  // Manejo de comandos
  useEffect(() => {
    if (value === '/') {
      setShowCommands(true);
    } else if (!value.startsWith('/')) {
      setShowCommands(false);
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowCommands(false);
    }
  };

  return (
    <div className="relative">
      <form onSubmit={onSubmit} className="flex space-x-2 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg 
              focus:ring-2 focus:ring-blue-500 focus:border-transparent 
              bg-white dark:bg-gray-700 text-gray-900 dark:text-white
              placeholder-gray-400 dark:placeholder-gray-500
              disabled:opacity-60 disabled:cursor-not-allowed
              transition-all"
            disabled={isLoading}
            aria-label="Mensaje"
            role="combobox"
            aria-expanded={showCommands}
            aria-controls="command-list"
            aria-autocomplete="list"
          />
          <Command className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg 
            hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors 
            flex items-center justify-center min-w-[48px]
            disabled:opacity-60 disabled:cursor-not-allowed
            shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </motion.button>
      </form>

      {/* Lista de comandos */}
      <AnimatePresence>
        {showCommands && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-full left-4 right-4 mb-2 bg-white dark:bg-gray-800 
              rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 
              overflow-hidden z-10"
          >
            <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-2">
              <Command className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Comandos disponibles</span>
            </div>
            <div className="max-h-48 overflow-y-auto" role="listbox" id="command-list">
              {Object.entries(QUICK_COMMANDS).map(([command, description]) => (
                <button
                  key={command}
                  onClick={() => {
                    onCommandSelect(command);
                    setShowCommands(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 
                    flex items-center space-x-2 transition-colors"
                  role="option"
                >
                  <span className="font-mono text-blue-500 dark:text-blue-400">{command}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{description}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MessageList = ({
  messages,
  messagesEndRef,
  onMessageFeedback,
  onRegenerateMessage
}) => (
  <motion.div
    className="flex-1 overflow-y-auto py-4 space-y-4 px-4 scroll-smooth"
    initial={false}
  >
    <AnimatePresence>
      {messages.map((message) => (
        <motion.div
          key={message.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          <ChatMessage
            message={message}
            onFeedback={(feedback) => onMessageFeedback(message.id, feedback)}
            onRegenerate={() => onRegenerateMessage(message.id)}
          />
        </motion.div>
      ))}
    </AnimatePresence>
    <div ref={messagesEndRef} />
  </motion.div>
);

const QuickActions = ({ onActionSelect, onClearConversation }) => (
  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-2 overflow-x-auto">
    <button
      onClick={() => onActionSelect('example')}
      className="flex items-center space-x-1 px-3 py-1 bg-gray-100 dark:bg-gray-800 
        rounded-full text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-200 
        dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
    >
      <Sparkles className="w-4 h-4" />
      <span>Ejemplo</span>
    </button>
    <button
      onClick={() => onActionSelect('recent')}
      className="flex items-center space-x-1 px-3 py-1 bg-gray-100 dark:bg-gray-800 
        rounded-full text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-200 
        dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
    >
      <Clock className="w-4 h-4" />
      <span>Recientes</span>
    </button>
    <button
      onClick={onClearConversation}
      className="flex items-center space-x-1 px-3 py-1 bg-gray-100 dark:bg-gray-800 
        rounded-full text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-200 
        dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
    >
      <Trash2 className="w-4 h-4" />
      <span>Limpiar</span>
    </button>
  </div>
);

export function AIChat() {
  const { user } = useAuthStore();
  const {
    messages,
    sendMessage,
    updateMessageFeedback,
    regenerateMessage,
    clearConversation
  } = useChatStore();
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Simulación de "está escribiendo"
  const handleTyping = () => {
    setIsTyping(true);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const message = newMessage.trim();

    if (!message || isLoading) return;

    try {
      setIsLoading(true);
      await sendMessage(message);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommandSelect = (command) => {
    switch (command) {
      case '/limpiar':
        clearConversation();
        break;
      case '/reiniciar':
        clearConversation();
        break;
      case '/ejemplo':
        setNewMessage('¿Cómo puedo mantener la calma en situaciones difíciles?');
        break;
      default:
        setNewMessage(command);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'example':
        setNewMessage('¿Qué es la filosofía estoica?');
        break;
      case 'recent':
        // Futura implementación de historial reciente
        break;
    }
  };

  const handleMessageFeedback = (messageId, feedback) => {
    updateMessageFeedback(messageId, feedback);
  };

  const handleRegenerateMessage = (messageId) => {
    regenerateMessage(messageId);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <QuickActions
        onActionSelect={handleQuickAction}
        onClearConversation={clearConversation}
      />

      {messages.length === 0 ? (
        <EmptyState
          message="¡Bienvenido a tu espacio de reflexión!"
          subMessage="Estoy aquí para guiarte en tu camino hacia la sabiduría estoica. ¿Sobre qué te gustaría reflexionar hoy?"
        />
      ) : (
        <MessageList
          messages={messages}
          messagesEndRef={messagesEndRef}
          onMessageFeedback={handleMessageFeedback}
          onRegenerateMessage={handleRegenerateMessage}
        />
      )}

      {isTyping && (
        <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
          El asistente está escribiendo...
        </div>
      )}

      <MessageInput
        value={newMessage}
        onChange={(e) => {
          setNewMessage(e.target.value);
          handleTyping();
        }}
        onSubmit={handleSubmit}
        onCommandSelect={handleCommandSelect}
        isLoading={isLoading}
        placeholder="Escribe tu mensaje... (usa / para comandos)"
      />
    </div>
  );
}

export default AIChat;